import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import Form, { Rule } from 'epn-ui/umd/dist/components/Form';
import InputText from 'epn-ui/umd/dist/components/InputText';
import Select, { DefaultOptionType } from 'epn-ui/umd/dist/components/Select';
import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';
import Radio from 'epn-ui/umd/dist/components/Radio';

import routes from '@config/routes.json';
import useI18n from '@i18n';
import { IGetTrafficSourcesCategoriesFormatted, IGetTrafficSourcesDataFormatted } from '@api/types';

import { isTelegramTrafficSource, isTelegramUrl, validateUrl } from './utils';
import styles from './styles.module.scss';
import TrafficButton from './TrafficButton';
import { IAddPlacementForm, IPlacementAddFormProps } from '../../interfaces';
import {
    DEFAULT_SOURCES_COUNT,
    DEFAULT_VALUES,
    SOCIAL_CATEGORY_ID,
    WEBSITE_CATEGORY_ID,
} from '../../constants';

const AddPlacementForm: React.FC<IPlacementAddFormProps> = ({
    offers,
    trafficSources,
    trafficCategories,
    isLoading,
    createInProgress,
    onSubmit,
    onNext,
    afterReg,
}) => {
    const { t, locale } = useI18n('Placements');
    const { t: tCommon } = useI18n('Common');
    const router = useRouter();
    const { query } = useRouter();
    const [form] = Form.useForm<IAddPlacementForm>();
    const [category, setCategory] = useState(1);
    const [selectedSource, setSelectedSource] = useState('0');
    const [showNext, setShowNext] = useState(false);
    const [formNotValid, setFormNotValid] = useState(true);

    const { offerId: urlOfferId } = query;

    const filterOption = useCallback((input: string, option: { title: string }): boolean => {
        return option?.title?.toLowerCase().includes(input.toLowerCase());
    }, []);

    const sortedCategories: IGetTrafficSourcesCategoriesFormatted[] = useMemo(
        () => trafficCategories?.sort((a, b) => a.priority - b.priority),
        [trafficCategories],
    );

    const trafficSourcesByCategory: IGetTrafficSourcesDataFormatted[] = useMemo(
        () =>
            trafficSources
                ?.filter(source => source.categoryId === category)
                ?.sort((a, b) => a.priority - b.priority),
        [category, trafficSources],
    );

    const firstTrafficSources: IGetTrafficSourcesDataFormatted[] = useMemo(
        () => trafficSourcesByCategory?.slice(0, DEFAULT_SOURCES_COUNT),
        [trafficSourcesByCategory],
    );

    const nextTrafficSources: IGetTrafficSourcesDataFormatted[] = useMemo(
        () => trafficSourcesByCategory?.slice(DEFAULT_SOURCES_COUNT),
        [trafficSourcesByCategory],
    );

    const isSocialOrWebsite: boolean = useMemo(
        () => category === SOCIAL_CATEGORY_ID || category === WEBSITE_CATEGORY_ID,
        [category],
    );

    const validateForm = (): void => {
        const values = form.getFieldsValue();
        const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
        const offerField = afterReg ? values.offer : true;

        const allFieldsFilled = values.url && values.trafficSource && offerField;

        setFormNotValid(hasErrors || !allFieldsFilled);
    };

    const onSelectSource = (id: string): void => {
        form.setFieldValue('trafficSource', id);
        form.setFieldValue('url', '');
        setSelectedSource(id);
        validateForm();
    };

    useEffect(() => {
        if (urlOfferId) form.setFieldValue('offer', +urlOfferId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlOfferId, offers]);

    return (
        <Loader loading={isLoading}>
            <Form
                form={form}
                initialValues={{
                    url: DEFAULT_VALUES.url,
                    offer: DEFAULT_VALUES.offer,
                    trafficSource: DEFAULT_VALUES.trafficSource,
                    trafficSourceCategory: DEFAULT_VALUES.trafficSourceCategory,
                }}
                onFinish={(values: IAddPlacementForm) => {
                    if (isSocialOrWebsite) onNext(values);
                    else onSubmit(values);
                }}
                id="add placement modal"
                onFieldsChange={validateForm}
            >
                <Form.Item name="trafficSourceCategory">
                    <Skeleton loading={isLoading}>
                        <Radio.Group
                            onChange={e => {
                                setCategory(e.target.value);
                                form.setFieldValue('trafficSourceCategory', e.target.value);
                            }}
                            buttonStyle="solid"
                            className={`${styles.radio} hide-scrollbar`}
                            value={category}
                        >
                            {sortedCategories?.map(item => (
                                <Radio.Button value={item.id} key={item.id}>
                                    {item.name}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </Skeleton>
                </Form.Item>

                <Form.Item name="trafficSource">
                    <div className="flex flex-wrap mb-4 gap-4">
                        {firstTrafficSources?.map(source => (
                            <TrafficButton
                                key={source.id}
                                source={source}
                                selectedSourceId={selectedSource}
                                onSelect={onSelectSource}
                            />
                        ))}
                        {!!nextTrafficSources?.length && (
                            <button
                                className={`${styles.nextBtn} ${showNext ? styles.active : ''}`}
                                onClick={() => setShowNext(!showNext)}
                                type="button"
                            >
                                <p className="text-[14px]">{t('Other')}</p>
                                <FontAwesomeIcon icon={faAngleDown} />
                            </button>
                        )}
                        {showNext &&
                            nextTrafficSources?.map(source => (
                                <TrafficButton
                                    key={source.id}
                                    source={source}
                                    selectedSourceId={selectedSource}
                                    onSelect={onSelectSource}
                                />
                            ))}
                    </div>
                </Form.Item>

                <Form.Item
                    name="url"
                    label={t('Type a link to your traffic source')}
                    rules={[
                        {
                            required: true,
                            whitespace: true,
                            message: tCommon('Required field'),
                        },
                        {
                            validator: async (_rule: Rule, value: string | null | undefined) => {
                                const isValidUrl = validateUrl(value, t);
                                if (!isValidUrl.valid) {
                                    throw new Error(isValidUrl.message);
                                }

                                if (
                                    isTelegramUrl(value || '') &&
                                    !isTelegramTrafficSource(form.getFieldValue('trafficSource'))
                                ) {
                                    throw new Error(t('Choose Telegram as the placement type'));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <InputText
                        disabled={!form.getFieldValue('trafficSource')}
                        placeholder={t('For example: https://t.me/epn_channel')}
                    />
                </Form.Item>

                {afterReg && (
                    <Form.Item
                        name="offer"
                        label={tCommon('Choose offer')}
                        rules={[{ required: true, message: tCommon('Required field') }]}
                    >
                        <Select
                            options={(offers as unknown) as DefaultOptionType[]}
                            placeholder={t('Start typing the name of the offer')}
                            disabled={
                                !Array.isArray(offers) ||
                                offers.length === 0 ||
                                !form.getFieldValue('trafficSource')
                            }
                            loading={isLoading}
                            fieldNames={{
                                label: 'title',
                                value: 'id',
                            }}
                            filterOption={filterOption}
                            notFoundContent={tCommon('Nothing found')}
                        />
                    </Form.Item>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                        htmlType="submit"
                        type="primary"
                        className="w-full md:w-auto min-w-[140px]"
                        disabled={isLoading || createInProgress || formNotValid}
                        loading={createInProgress}
                    >
                        {isSocialOrWebsite ? t('Next Button') : t('Connect button')}
                    </Button>

                    {afterReg ? (
                        <Button
                            type="default"
                            className="w-full md:w-auto min-w-[140px]"
                            onClick={() => router.push(`/${locale}${routes.placements.list}`)}
                        >
                            {t('Traffic sources')}
                        </Button>
                    ) : (
                        <Button
                            type="default"
                            className="w-full md:w-auto min-w-[140px]"
                            onClick={() => router.push(`/${locale}${routes.offers}`)}
                        >
                            {t('Not now')}
                        </Button>
                    )}
                </div>
                {category === SOCIAL_CATEGORY_ID && locale === 'ru' && (
                    <p className="mt-8 text-blueGray-300 text-[12px]">
                        <span className="text-yellow-400">*</span> - социальная сеть, принадлежащая
                        организации Meta, чья деятельность запрещена на территории Российской
                        Федерации
                    </p>
                )}
            </Form>
        </Loader>
    );
};

export default AddPlacementForm;
